import { useRef, useState, useEffect } from "react"
import type { NodeViewProps } from "@tiptap/react"
import { NodeViewWrapper } from "@tiptap/react"
import { Button } from "../../tiptap-ui-primitive/button"
import { CloseIcon } from "../../tiptap-icons/close-icon"
import "./image-upload-node.scss"
import { isValidPosition } from "../../lib/tiptap-utils"

interface ImageItem {
  id: string
  file: File
  progress: number
  status: "uploading" | "success" | "error"
  url?: string
  previewUrl?: string
  abortController?: AbortController
}

interface ImageUploadOptions {
  maxSize: number
  limit: number
  accept: string
  upload: (file: File, onProgress: (event: { progress: number }) => void, signal: AbortSignal) => Promise<string>
  onSuccess?: (url: string) => void
  onError?: (error: Error) => void
}

function useImageUpload(options: ImageUploadOptions) {
  const [imageItems, setImageItems] = useState<ImageItem[]>([])

  const uploadImage = async (file: File): Promise<string | null> => {
    if (options.maxSize && file.size > options.maxSize) {
      options.onError?.(new Error(`Размер файла превышает ${options.maxSize / 1024 / 1024}МБ`))
      return null
    }

    const abortController = new AbortController()
    const imageId = crypto.randomUUID()
    
    // Создаем превью изображения
    const previewUrl = URL.createObjectURL(file)

    setImageItems((prev) => [...prev, {
      id: imageId,
      file,
      progress: 0,
      status: "uploading",
      previewUrl,
      abortController,
    }])

    try {
      if (!options.upload) {
        throw new Error("Функция загрузки не определена")
      }

      const url = await options.upload(
        file,
        (event) => {
          setImageItems((prev) =>
            prev.map((item) =>
              item.id === imageId ? { ...item, progress: event.progress } : item
            )
          )
        },
        abortController.signal
      )

      if (!url) throw new Error("Ошибка загрузки: URL не получен")

      if (!abortController.signal.aborted) {
        setImageItems((prev) =>
          prev.map((item) =>
            item.id === imageId ? { ...item, status: "success", url, progress: 100 } : item
          )
        )
        options.onSuccess?.(url)
        return url
      }

      return null
    } catch (error) {
      if (!abortController.signal.aborted) {
        setImageItems((prev) =>
          prev.map((item) =>
            item.id === imageId ? { ...item, status: "error", progress: 0 } : item
          )
        )
        options.onError?.(error instanceof Error ? error : new Error("Ошибка загрузки"))
      }
      return null
    }
  }

  const uploadImages = async (files: File[]): Promise<string[]> => {
    if (!files || files.length === 0) {
      options.onError?.(new Error("Нет файлов для загрузки"))
      return []
    }

    if (options.limit && files.length > options.limit) {
      options.onError?.(new Error(`Максимум ${options.limit} файлов`))
      return []
    }

    const results = await Promise.all(files.map((file) => uploadImage(file)))
    return results.filter((url): url is string => url !== null)
  }

  const removeImageItem = (imageId: string) => {
    setImageItems((prev) => {
      const item = prev.find((i) => i.id === imageId)
      item?.abortController?.abort()
      // Освобождаем URL превью
      if (item?.previewUrl) {
        URL.revokeObjectURL(item.previewUrl)
      }
      return prev.filter((i) => i.id !== imageId)
    })
  }

  return { imageItems, uploadImages, removeImageItem }
}

const ImageUploadIcon: React.FC = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="tiptap-image-upload-icon">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
    <path d="M21 15L16 10L5 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

export const ImageUploadNode: React.FC<NodeViewProps> = (props) => {
  const { accept, limit, maxSize } = props.node.attrs
  const inputRef = useRef<HTMLInputElement>(null)
  const extension = props.extension

  const uploadOptions: ImageUploadOptions = {
    maxSize: maxSize || 5 * 1024 * 1024,
    limit: limit || 1,
    accept: accept || "image/*",
    upload: extension.options.upload,
    onSuccess: extension.options.onSuccess,
    onError: extension.options.onError,
  }

  const { imageItems, uploadImages, removeImageItem } = useImageUpload(uploadOptions)

  const handleUpload = async (files: File[]) => {
    const urls = await uploadImages(files)

    if (urls.length > 0) {
      const pos = props.getPos()

      if (isValidPosition(pos)) {
        const images = urls.map((url) => ({
          type: extension.options.type || "image",
          attrs: { src: url },
        }))

        props.editor
          .chain()
          .focus()
          .deleteRange({ from: pos, to: pos + props.node.nodeSize })
          .insertContentAt(pos, images)
          .run()
      }
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    handleUpload(Array.from(files))
  }

  const handleClick = () => {
    if (inputRef.current && imageItems.length === 0) {
      inputRef.current.value = ""
      inputRef.current.click()
    }
  }

  const handleRemoveNode = (e: React.MouseEvent) => {
    e.stopPropagation()
    const pos = props.getPos()
    if (isValidPosition(pos)) {
      props.editor
        .chain()
        .focus()
        .deleteRange({ from: pos, to: pos + props.node.nodeSize })
        .run()
    }
  }

  return (
    <NodeViewWrapper className="tiptap-image-upload" onClick={handleClick}>
      {imageItems.length === 0 && (
        <div className="tiptap-image-upload-dropzone">
          <Button 
            className="tiptap-upload-close-btn" 
            data-style="ghost" 
            onClick={handleRemoveNode}
            title="Отменить"
          >
            <CloseIcon className="tiptap-button-icon" />
          </Button>
          <ImageUploadIcon />
          <div className="tiptap-image-upload-text">
            <span>Нажмите для загрузки</span> или перетащите файл
          </div>
          <div className="tiptap-image-upload-subtext">
            Макс. {uploadOptions.maxSize / 1024 / 1024}МБ
          </div>
        </div>
      )}

      {imageItems.length > 0 && (
        <div className="tiptap-image-upload-previews">
          {imageItems.map((item) => (
            <div key={item.id} className="tiptap-image-upload-preview-card">
              <div className="tiptap-image-upload-preview-image">
                {item.previewUrl && (
                  <img src={item.previewUrl} alt={item.file.name} />
                )}
                {item.status === "uploading" && (
                  <div className="tiptap-image-upload-overlay">
                    <div className="tiptap-image-upload-progress-bar">
                      <div 
                        className="tiptap-image-upload-progress-fill" 
                        style={{ width: `${item.progress}%` }} 
                      />
                    </div>
                    <span className="tiptap-image-upload-progress-text">{item.progress}%</span>
                  </div>
                )}
                {item.status === "error" && (
                  <div className="tiptap-image-upload-overlay tiptap-image-upload-error">
                    <span>Ошибка загрузки</span>
                  </div>
                )}
              </div>
              <div className="tiptap-image-upload-preview-info">
                <span className="tiptap-image-upload-preview-name">{item.file.name}</span>
                <Button data-style="ghost" onClick={(e) => { e.stopPropagation(); removeImageItem(item.id) }}>
                  <CloseIcon className="tiptap-button-icon" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={limit > 1}
        onChange={handleChange}
        onClick={(e) => e.stopPropagation()}
      />
    </NodeViewWrapper>
  )
}
